import { action, makeObservable, observable, reaction, runInAction } from 'mobx'
import type { Room } from 'livekit-client'
import { Cubit } from './core'
import type { MeetingCubit } from './MeetingCubit'
import {
  getBackgroundBlurPreference,
  setBackgroundBlurPreference,
} from '../util/backgroundBlurPreference'

// Lazily-loaded helpers so neither `@livekit/track-processors` nor any
// runtime livekit-client APIs are pulled into this module's static graph.
// `import type` keeps this purely at the type level — the applier module is
// only fetched at runtime via `await import(...)` inside `loadApplier`.
import type * as BlurApplier from './meeting/MeetingBackgroundBlurApplier'

export class MeetingBackgroundBlurCubit extends Cubit {
  meetingCubit: MeetingCubit

  // User-stated intent. Survives camera on/off and AV-check → meeting
  // transitions: the cubit reconciles by applying or removing the processor
  // whenever a camera track is published.
  @observable
  blurEnabled: boolean = getBackgroundBlurPreference()

  @observable
  isToggling: boolean = false

  private processorApplied: boolean = false
  private reconcilePromise: Promise<void> | null = null
  private reconcileNeeded: boolean = false
  private roomCleanup: VoidFunction | null = null
  private applierPromise: Promise<typeof BlurApplier> | null = null

  constructor(meetingCubit: MeetingCubit) {
    super()
    this.meetingCubit = meetingCubit
    makeObservable(this)
  }

  initialize() {
    this.disposers.push(
      reaction(
        () => this.blurEnabled,
        () => this.reconcile()
      )
    )
    this.disposers.push(
      reaction(
        () => this.meetingCubit.livekitRoom,
        (room) => this.attachRoomListener(room),
        { fireImmediately: true }
      )
    )
  }

  @action
  toggleBlur() {
    this.setBlurEnabled(!this.blurEnabled)
  }

  @action
  setBlurEnabled(value: boolean) {
    if (value === this.blurEnabled) return
    const radius =
      this.meetingCubit.repository.featureFlags.effectiveBackgroundBlurRadius
    if (value && radius <= 0) return
    this.blurEnabled = value
    setBackgroundBlurPreference(value)
    this.meetingCubit.logEvent(
      value
        ? 'meeting_background_blur_enabled'
        : 'meeting_background_blur_disabled'
    )
  }

  private loadApplier(): Promise<typeof BlurApplier> {
    if (!this.applierPromise) {
      this.applierPromise = import('./meeting/MeetingBackgroundBlurApplier')
    }
    return this.applierPromise
  }

  private attachRoomListener(room: Room | null) {
    if (this.roomCleanup) {
      this.roomCleanup()
      this.roomCleanup = null
    }
    if (!room) return

    const onPublished = () => this.reconcile()
    const onUnpublished = () => {
      // Track gone — drop our record so reconcile reapplies on the next
      // publish. User intent (blurEnabled) is preserved.
      this.processorApplied = false
      this.reconcile()
    }
    room.localParticipant.on('localTrackPublished', onPublished)
    room.localParticipant.on('localTrackUnpublished', onUnpublished)
    this.roomCleanup = () => {
      room.localParticipant.off('localTrackPublished', onPublished)
      room.localParticipant.off('localTrackUnpublished', onUnpublished)
    }
    this.reconcile()
  }

  private reconcile() {
    // If a reconcile is already in flight, just mark that another pass is
    // needed. The running loop will re-run after the current iteration so
    // it picks up the latest state — preventing rapid toggles from getting
    // dropped (the in-flight pass observes stale state and would otherwise
    // leave the processor in the wrong state for good).
    if (this.reconcilePromise) {
      this.reconcileNeeded = true
      return this.reconcilePromise
    }
    runInAction(() => {
      this.isToggling = true
    })
    this.reconcilePromise = (async () => {
      do {
        this.reconcileNeeded = false
        try {
          await this._reconcile()
        } catch {
          // swallow — never let a transient failure break the loop
        }
      } while (this.reconcileNeeded)
      runInAction(() => {
        this.reconcilePromise = null
        this.isToggling = false
      })
    })()
    return this.reconcilePromise
  }

  private async _reconcile() {
    const room = this.meetingCubit.livekitRoom
    if (!room) return

    const radius =
      this.meetingCubit.repository.featureFlags.effectiveBackgroundBlurRadius
    const want = this.blurEnabled && radius > 0

    if (want && !this.processorApplied) {
      const { applyBlurToCameraTrack } = await this.loadApplier()
      const applied = await applyBlurToCameraTrack(room, radius)
      this.processorApplied = applied
    } else if (!want && this.processorApplied) {
      const { removeBlurFromCameraTrack } = await this.loadApplier()
      await removeBlurFromCameraTrack(room)
      this.processorApplied = false
    }
  }

  dispose() {
    if (this.roomCleanup) {
      this.roomCleanup()
      this.roomCleanup = null
    }
    if (this.processorApplied) {
      const room = this.meetingCubit.livekitRoom
      if (room) {
        // Fire-and-forget cleanup. The applier is already loaded if we got
        // this far (processorApplied was set after a successful apply).
        this.loadApplier()
          .then(({ removeBlurFromCameraTrack }) =>
            removeBlurFromCameraTrack(room)
          )
          .catch(() => {})
      }
      this.processorApplied = false
    }
    return super.dispose()
  }
}
